// React
import { Dispatch, ReactElement, SetStateAction } from 'react';
// Utils
import { getColorStyle } from '@utmb/utils/colors';
import { useTranslation } from 'next-i18next';
import { v4 as uuidv4 } from 'uuid';
import ResizeObserver from 'react-resize-observer';
// Interfaces
import { ColorTypesEnum } from '@utmb/types/colors';
import { IconsEnum } from '@utmb/types/icons';
import { RaceItemView, StatView } from '@utmb/types/index';
// Design System
import { Image } from '@utmb/design-system/image/Image';
import { Font } from '@utmb/design-system/text/font';
import { Link } from '@utmb/design-system/link/link';
import { Icon } from '@utmb/design-system/icons/Icon';
import { Spacer } from '@utmb/design-system/spacer/Spacer';
// Features
import { PlaygroundIcon } from '@utmb/features/races/races-list/playgrounds/PlaygroundIcon';
import Stat from '@utmb/features/races/races-list/Stat';
// Styles
import styles from './races-list.module.scss';

interface ContentCardDesktopProps extends RaceItemView {
    colorToUse: string;
    raceCardIsOpen: boolean;
    setShowDialog: Dispatch<SetStateAction<boolean>>;
    setRaceCardIsOpen: Dispatch<SetStateAction<boolean>>;
}

const ContentCard = (props: ContentCardDesktopProps): ReactElement => {
    const { logo, name, startLocation, raceCardIsOpen, setRaceCardIsOpen, details, slug, playgrounds, raceTheme, setShowDialog, colorToUse } = props;
    const { statsUp = [], statsDown = [] } = details;

    const { t } = useTranslation();

    return (
        <div className={styles.wrapper_content}>
            <ResizeObserver
                onResize={() => {
                    if (!raceCardIsOpen && window.innerWidth > 1200) setRaceCardIsOpen(true);
                    else if (raceCardIsOpen && window.innerWidth <= 1200) setRaceCardIsOpen(true);
                }}
            />
            <div className={styles.container_content} style={{ borderColor: raceTheme }}>
                <div className={`row ${styles.logo_row}`}>
                    <div className="col-6 col-xl-9">
                        {/* Logo or Race category */}
                        {logo ? (
                            <Image layout="fixed" objectFit="contain" priority width={172} height={57} image={logo} alt={name} />
                        ) : (
                            <Font.OxaniumBold mobile="18" desktop="32" className="lh-30 lh-d-40" as="span">
                                <span
                                    style={{
                                        ...(colorToUse && {
                                            color: colorToUse,
                                        }),
                                    }}
                                >
                                    {name}
                                </span>
                            </Font.OxaniumBold>
                        )}
                        {/* City Name */}
                        <div className={`col-12 col-xl-3 ${styles.card_location_mobile}`}>
                            <Icon variant={IconsEnum.PIN} clss={'color-secondary'} />
                            <Font.FuturaBook mobile="14">{startLocation}</Font.FuturaBook>
                        </div>
                    </div>
                    <div className={`col-3 ${styles.card_location_desktop}`}>
                        <Icon variant={IconsEnum.PIN} clss={'color-secondary'} />
                        <Font.FuturaBook mobile="14">{startLocation}</Font.FuturaBook>
                    </div>
                    <div className={`col-6 ${styles.open_detail_toggler}`}>
                        <button onClick={() => setRaceCardIsOpen(!raceCardIsOpen)} className={styles.button_chevron}>
                            {raceCardIsOpen && (
                                <>
                                    <Font.FuturaBook mobile="14">{t('races.showLess')}</Font.FuturaBook>{' '}
                                    <Icon variant={IconsEnum.CHEVRON_UP} clss={styles.icon_chevron} />
                                </>
                            )}
                            {!raceCardIsOpen && (
                                <>
                                    <Font.FuturaBook mobile="14">{t('races.showMore')}</Font.FuturaBook>{' '}
                                    <Icon variant={IconsEnum.CHEVRON_DOWN} clss={styles.icon_chevron} />
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {raceCardIsOpen && (
                    <div>
                        {/* Stats Resume */}
                        <div aria-hidden="true">
                            <Spacer.size20 />
                        </div>
                        <div className="row">
                            {statsUp.map((stat: StatView) => (
                                <div key={uuidv4()} className={`col-6 ${styles.wrapped_stat}`}>
                                    <Stat {...stat} raceTheme={colorToUse} key={uuidv4()} />
                                </div>
                            ))}
                        </div>

                        {/* Stats Line */}
                        <div aria-hidden="true">
                            <Spacer.size10 />
                        </div>
                        <div className="row">
                            {statsDown.map((stat: StatView) => (
                                <div key={uuidv4()} className={`col-6 col-xl-3 ${styles.wrapped_stat}`}>
                                    <Stat {...stat} />
                                    <div aria-hidden="true">
                                        <Spacer.size10 />
                                    </div>
                                </div>
                            ))}
                            {playgrounds && playgrounds.length > 0 && (
                                <div className={styles.playground_wrapper}>
                                    <Font.OxaniumRegular mobile="12" desktop="14" className={styles.playground_label}>
                                        {t('races.playground')}
                                    </Font.OxaniumRegular>
                                    <button
                                        onClick={() => setShowDialog(true)}
                                        className={styles.playground_button}
                                        style={{
                                            ...(colorToUse && {
                                                color: colorToUse,
                                                borderColor: colorToUse,
                                            }),
                                        }}
                                    >
                                        {playgrounds.length > 0 && <PlaygroundIcon color={colorToUse} icon={playgrounds[0]} />}
                                        {playgrounds.length > 1 && (
                                            <Font.OxaniumBold mobile="18" desktop="20" className={styles.playground_qty}>
                                                +{playgrounds.length - 1}
                                            </Font.OxaniumBold>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Button to details */}
                        <div aria-hidden="true">
                            <Spacer.size20 />
                        </div>
                        <div className={`${styles.actions_row} row`}>
                            <Link.Button
                                label={t('racesList.labelLinkDetail')}
                                href={`/races/${slug}`}
                                bgcolor={getColorStyle(ColorTypesEnum.PRIMARY)}
                                underline={false}
                                variant="right"
                                alignEnd={true}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContentCard;
